import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  ArrowLeft, Sparkles, MessageSquareText, Mail, Check, Trash2, Loader2, PenLine, X, Users, BarChart3, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const FEELING_META = {
  sad: { label: 'Sad', emoji: '💧', tint: '#5B7C99' },
  anxious: { label: 'Anxious', emoji: '🌫', tint: '#8C867D' },
  angry: { label: 'Angry', emoji: '🔥', tint: '#B34E1F' },
  confused: { label: 'Confused', emoji: '🤔', tint: '#B38A2E' },
  hopeful: { label: 'Hopeful', emoji: '☀️', tint: '#E8C265' },
  hurt: { label: 'Hurt', emoji: '💔', tint: '#B34E1F' },
};
const REL_META = {
  spouse: { label: 'Spouse / Partner', emoji: '💍' },
  family: { label: 'Family', emoji: '🏡' },
  friend: { label: 'Friend', emoji: '🤝' },
  child: { label: 'Child', emoji: '🌱' },
  myself: { label: 'Myself', emoji: '🪞' },
};

export default function AdminArticleRequests() {
  const [requests, setRequests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('grouped'); // grouped | open | written | all
  const [linking, setLinking] = useState({});     // req_id -> article_id being picked
  const [groupLinking, setGroupLinking] = useState({}); // group key -> article_id
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});
  const [showStats, setShowStats] = useState(true);

  useEffect(() => {
    load();
    api.get('/articles?status=published').then(({ data }) => setArticles(data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [r, g, a] = await Promise.all([
        api.get('/admin/article-requests'),
        api.get('/admin/article-requests/grouped'),
        api.get('/admin/article-requests/analytics'),
      ]);
      setRequests(r.data || []);
      setGroups(g.data || []);
      setAnalytics(a.data || null);
    } catch (_) {
      toast.error('Could not load requests');
    } finally { setLoading(false); }
  };

  const setStatus = async (id, status, extra = {}) => {
    setBusy(prev => ({ ...prev, [id]: true }));
    try {
      const { data } = await api.put(`/admin/article-requests/${id}/status`, { status, ...extra });
      if (data.email_sent) toast.success('Marked written — the requester has been emailed.');
      else if (status === 'written') toast.success('Marked written.');
      else toast.success('Updated');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    } finally { setBusy(prev => ({ ...prev, [id]: false })); }
  };

  const bulkMarkWritten = async (group) => {
    const articleId = groupLinking[group.key];
    if (!articleId) { toast.error('Pick a published article first'); return; }
    setBusy(prev => ({ ...prev, [group.key]: true }));
    try {
      const { data } = await api.post('/admin/article-requests/bulk-status', {
        ids: group.requester_ids,
        status: 'written',
        article_id: articleId,
      });
      toast.success(`${data.updated} requests marked · ${data.emails_sent} people emailed`);
      setGroupLinking(prev => { const c = { ...prev }; delete c[group.key]; return c; });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update group');
    } finally { setBusy(prev => ({ ...prev, [group.key]: false })); }
  };

  const removeRequest = async (id) => {
    if (!window.confirm('Delete this request? This cannot be undone.')) return;
    setBusy(prev => ({ ...prev, [id]: true }));
    try {
      await api.delete(`/admin/article-requests/${id}`);
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete');
    } finally { setBusy(prev => ({ ...prev, [id]: false })); }
  };

  const flatFiltered = useMemo(() => requests.filter(r => {
    if (filter === 'open') return r.status !== 'written' && r.status !== 'declined';
    if (filter === 'written') return r.status === 'written';
    return true;
  }), [requests, filter]);

  const showGrouped = filter === 'grouped';

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="requests-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#78A18E22' }}>
            <MessageSquareText className="w-6 h-6" style={{ color: '#5C715E' }} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C715E', letterSpacing: '0.15em' }}>Journey</p>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Content Requests</h1>
          </div>
        </div>
        <p className="mb-8" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Every unmet request tells you what to write next. When you publish an article that answers one, mark it <b>Written</b> and the requester will be emailed automatically.</p>

        {/* Analytics header (collapsible) */}
        {analytics && (
          <div className="p-5 rounded-3xl mb-6" style={{ background: 'linear-gradient(135deg, #2C2724 0%, #4A3F35 100%)', color: '#FDFBF7' }} data-testid="requests-analytics">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" style={{ color: '#E8C265' }} />
                <p className="text-xs font-semibold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif', color: '#E8C265', letterSpacing: '0.15em' }}>What people are asking for</p>
              </div>
              <button onClick={() => setShowStats(!showStats)} className="p-1 rounded opacity-70 hover:opacity-100" data-testid="analytics-toggle">
                {showStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-baseline gap-6 flex-wrap mb-3">
              <div><span className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>{analytics.open}</span> <span className="text-sm" style={{ color: '#B8AC98' }}>open</span></div>
              <div><span className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>{analytics.written}</span> <span className="text-xs" style={{ color: '#B8AC98' }}>written</span></div>
              <div><span className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>{analytics.total}</span> <span className="text-xs" style={{ color: '#B8AC98' }}>all-time</span></div>
            </div>
            {showStats && analytics.top_combos.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-widest mb-2 mt-4" style={{ fontFamily: 'Manrope, sans-serif', color: '#B8AC98', letterSpacing: '0.14em' }}>Top open combos (write these first)</p>
                <div className="flex flex-wrap gap-2">
                  {analytics.top_combos.slice(0, 6).map((c, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: '#3E3630', fontFamily: 'Manrope, sans-serif', fontSize: '12px' }}>
                      <span style={{ color: '#E8C265', fontWeight: 700 }}>{FEELING_META[c.feeling]?.emoji} {FEELING_META[c.feeling]?.label}</span>
                      <span style={{ color: '#B8AC98' }}>·</span>
                      <span style={{ color: '#FDFBF7', fontWeight: 700 }}>{REL_META[c.relationship]?.emoji} {REL_META[c.relationship]?.label}</span>
                      <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px]" style={{ backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 800 }}>×{c.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Filter tabs */}
        <div className="inline-flex items-center gap-1 p-1 rounded-full mb-6" style={{ backgroundColor: '#F3EFE7' }} data-testid="requests-filter">
          {[
            { key: 'grouped', label: 'Grouped', icon: Users },
            { key: 'open', label: 'Open' },
            { key: 'written', label: 'Written' },
            { key: 'all', label: 'All' },
          ].map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)}
              className="px-4 py-1.5 rounded-full text-sm transition flex items-center gap-1.5"
              style={{
                fontFamily: 'Manrope, sans-serif', fontWeight: 700,
                backgroundColor: filter === t.key ? '#2C2724' : 'transparent',
                color: filter === t.key ? '#FDFBF7' : '#595553',
              }}
              data-testid={`filter-${t.key}`}>
              {t.icon && <t.icon className="w-3.5 h-3.5" />}
              {t.label}
            </button>
          ))}
          <span className="ml-2 text-xs px-3 py-1 rounded-full" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FFFFFF', color: '#8C867D' }}>{showGrouped ? `${groups.length} groups` : `${flatFiltered.length}`}</span>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: '#5C715E' }} /></div>
        ) : showGrouped ? (
          groups.length === 0 ? (
            <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="groups-empty">
              <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: '#78A18E' }} />
              <p style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>Nothing to group — you're all caught up.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="groups-list">
              {groups.map(g => {
                const f = FEELING_META[g.feeling] || {};
                const rel = REL_META[g.relationship] || {};
                const isExpanded = expanded[g.key];
                const isLinking = g.key in groupLinking;
                return (
                  <li key={g.key} className="p-5 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: g.count > 1 ? '1.5px solid #E57A44' : '1px solid #F0EBE0', boxShadow: g.count > 1 ? '0 8px 30px rgba(229, 122, 68, 0.10)' : '0 4px 20px rgba(0,0,0,0.03)' }} data-testid={`group-${g.key}`}>
                    <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ backgroundColor: `${f.tint}20`, color: f.tint, fontFamily: 'Manrope, sans-serif', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          <span>{f.emoji}</span> {f.label}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ backgroundColor: '#F3EFE7', color: '#595553', fontFamily: 'Manrope, sans-serif', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          <span>{rel.emoji}</span> {rel.label}
                        </span>
                        {g.count > 1 && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ background: 'linear-gradient(135deg, #E57A44 0%, #D96A34 100%)', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 800 }} data-testid={`group-count-${g.key}`}>
                            <Users className="w-3 h-3" /> {g.count} people asked
                          </span>
                        )}
                      </div>
                    </div>
                    <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>"{g.topic}"</h3>

                    {/* Preview 1st requester */}
                    {g.requests[0]?.description && (
                      <p className="text-sm mb-3 whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7, fontStyle: 'italic' }}>
                        <span style={{ color: '#8C867D', fontStyle: 'normal', fontFamily: 'Nunito, sans-serif', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{g.requests[0].user_name} said:</span>{' '}
                        {g.requests[0].description.slice(0, 200)}{g.requests[0].description.length > 200 ? '…' : ''}
                      </p>
                    )}

                    {/* Toggle: see all people in group */}
                    {g.count > 1 && (
                      <button
                        onClick={() => setExpanded(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                        className="text-xs mb-3 inline-flex items-center gap-1"
                        style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 700 }}
                        data-testid={`group-expand-${g.key}`}
                      >
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} {isExpanded ? 'Hide' : `See all ${g.count} people`}
                      </button>
                    )}
                    {isExpanded && (
                      <ul className="space-y-2 mb-3 pl-4" style={{ borderLeft: '2px solid #F0EBE0' }} data-testid={`group-people-${g.key}`}>
                        {g.requests.map(r => (
                          <li key={r.id} className="text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>
                            <span style={{ color: '#2C2A29', fontWeight: 700 }}>{r.user_name}</span>
                            <span style={{ color: '#8C867D' }}> · <a href={`mailto:${r.user_email}`} style={{ color: '#E57A44' }}>{r.user_email}</a> · {new Date(r.created_at).toLocaleDateString()}</span>
                            {r.description && <p className="mt-1" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.6, fontStyle: 'italic' }}>{r.description}</p>}
                          </li>
                        ))}
                        {g.count > g.requests.length && (
                          <li className="text-xs italic" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>…and {g.count - g.requests.length} more with similar wording</li>
                        )}
                      </ul>
                    )}

                    {/* Action bar */}
                    {isLinking ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={groupLinking[g.key]}
                          onChange={(e) => setGroupLinking(prev => ({ ...prev, [g.key]: e.target.value }))}
                          className="px-3 py-2 rounded-full text-sm border flex-1 min-w-[240px]"
                          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                          data-testid={`group-link-${g.key}`}
                        >
                          <option value="">Pick a published article…</option>
                          {articles.map(a => (
                            <option key={a.id} value={a.id}>{a.title}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => bulkMarkWritten(g)}
                          disabled={busy[g.key] || !groupLinking[g.key]}
                          className="px-4 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-50"
                          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFF', fontWeight: 700 }}
                          data-testid={`group-confirm-${g.key}`}
                        >
                          <Mail className="w-3.5 h-3.5" /> Notify all {g.count} & mark written
                        </button>
                        <button onClick={() => setGroupLinking(prev => { const c = { ...prev }; delete c[g.key]; return c; })} className="p-2 rounded-full" style={{ backgroundColor: '#F3EFE7' }}>
                          <X className="w-4 h-4" style={{ color: '#8C867D' }} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setGroupLinking(prev => ({ ...prev, [g.key]: '' }))}
                        className="px-4 py-2 rounded-full text-sm flex items-center gap-2 transition hover:-translate-y-0.5"
                        style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #E57A44 0%, #D96A34 100%)', color: '#FDFBF7', fontWeight: 700 }}
                        data-testid={`group-write-${g.key}`}
                      >
                        <PenLine className="w-3.5 h-3.5" /> Link article + notify {g.count > 1 ? `all ${g.count} people` : 'them'}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )
        ) : flatFiltered.length === 0 ? (
          <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="requests-empty">
            <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: '#78A18E' }} />
            <p style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>{filter === 'open' ? "You're all caught up — no open requests." : 'Nothing here yet.'}</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {flatFiltered.map(r => {
              const f = FEELING_META[r.feeling] || {};
              const rel = REL_META[r.relationship] || {};
              const isWritten = r.status === 'written';
              return (
                <li key={r.id} className="p-5 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', opacity: isWritten ? 0.7 : 1 }} data-testid={`request-${r.id}`}>
                  {/* Header pills */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ backgroundColor: `${f.tint}20`, color: f.tint, fontFamily: 'Manrope, sans-serif', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      <span>{f.emoji}</span> {f.label}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ backgroundColor: '#F3EFE7', color: '#595553', fontFamily: 'Manrope, sans-serif', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      <span>{rel.emoji}</span> {rel.label}
                    </span>
                    {isWritten && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: '#78A18E', color: '#FFF', fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 700 }}>
                        <Check className="w-3 h-3" /> Written · Notified
                      </span>
                    )}
                    <span className="ml-auto text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>

                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>"{r.topic}"</h3>
                  {r.description && (
                    <p className="text-sm mb-3 whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7, fontStyle: 'italic' }}>{r.description}</p>
                  )}

                  {/* Requester line */}
                  <p className="text-xs mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                    <Mail className="inline w-3 h-3 mr-1" /> {r.user_name} · <a href={`mailto:${r.user_email}`} style={{ color: '#E57A44' }}>{r.user_email}</a>
                  </p>

                  {/* Action bar */}
                  {!isWritten && (
                    <div className="flex flex-wrap items-center gap-2">
                      {linking[r.id] !== undefined ? (
                        <div className="flex items-center gap-2 flex-wrap w-full">
                          <select
                            value={linking[r.id]}
                            onChange={(e) => setLinking(prev => ({ ...prev, [r.id]: e.target.value }))}
                            className="px-3 py-2 rounded-full text-sm border flex-1 min-w-[240px]"
                            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                            data-testid={`link-article-${r.id}`}
                          >
                            <option value="">Pick a published article…</option>
                            {articles.map(a => (
                              <option key={a.id} value={a.id}>{a.title}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => setStatus(r.id, 'written', { article_id: linking[r.id] })}
                            disabled={busy[r.id] || !linking[r.id]}
                            className="px-4 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-50"
                            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFF', fontWeight: 700 }}
                            data-testid={`confirm-written-${r.id}`}
                          >
                            <Mail className="w-3.5 h-3.5" /> Mark written + notify
                          </button>
                          <button onClick={() => setLinking(prev => { const c = { ...prev }; delete c[r.id]; return c; })} className="p-2 rounded-full" style={{ backgroundColor: '#F3EFE7' }} data-testid={`cancel-link-${r.id}`}>
                            <X className="w-4 h-4" style={{ color: '#8C867D' }} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => setLinking(prev => ({ ...prev, [r.id]: '' }))}
                            className="px-4 py-2 rounded-full text-sm flex items-center gap-2 transition hover:-translate-y-0.5"
                            style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #E57A44 0%, #D96A34 100%)', color: '#FDFBF7', fontWeight: 700 }}
                            data-testid={`request-${r.id}-mark-written`}
                          >
                            <PenLine className="w-3.5 h-3.5" /> Link article + notify
                          </button>
                          {r.status !== 'writing' && (
                            <button onClick={() => setStatus(r.id, 'writing')} disabled={busy[r.id]}
                              className="px-4 py-2 rounded-full text-sm disabled:opacity-50"
                              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FBF3DC', color: '#B38A2E', fontWeight: 700 }}
                              data-testid={`request-${r.id}-writing`}>
                              Set to Writing
                            </button>
                          )}
                          <button onClick={() => setStatus(r.id, 'declined')} disabled={busy[r.id]}
                            className="px-4 py-2 rounded-full text-sm disabled:opacity-50"
                            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', color: '#8C867D', border: '1px solid #E6E2D8', fontWeight: 700 }}
                            data-testid={`request-${r.id}-decline`}>
                            Set aside
                          </button>
                          <button onClick={() => removeRequest(r.id)} disabled={busy[r.id]} className="ml-auto p-2 rounded-full opacity-60 hover:opacity-100" title="Delete" data-testid={`request-${r.id}-delete`}>
                            <Trash2 className="w-4 h-4" style={{ color: '#8C5E5E' }} />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {isWritten && r.article_id && (
                    <div className="flex items-center gap-2">
                      <Link to={`/article/${r.article_id}`} className="px-4 py-2 rounded-full text-sm inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', color: '#78A18E', border: '1px solid #78A18E', fontWeight: 700 }}>
                        View article →
                      </Link>
                      <button onClick={() => removeRequest(r.id)} className="ml-auto p-2 rounded-full opacity-60 hover:opacity-100" title="Delete">
                        <Trash2 className="w-4 h-4" style={{ color: '#8C5E5E' }} />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
}
